/**
 * /execution/installer-network — execution lens over the contractor list.
 *
 * Filters to `businessRole: "executor" | "both"` and surfaces execution-only
 * columns (vetting, score, callbacks 30d, holdbacks, probation).
 *
 * No writers. No financial fields exposed.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { computeExecutionScore } from "@/data/contractor/networkExecution/executionScore";
import { MIN_SAMPLE_FOR_DISPLAY } from "@/lib/analyticsThresholds";

const DAY = 86_400_000;

export default function ExecutionInstallerNetworkPage() {
  const { role } = useRole();
  const { contractors, jobs, callbacks, holdbackLedger } = useAppData();

  const rows = useMemo(() => {
    const cutoff = Date.now() - 30 * DAY;
    return contractors
      .filter((c) => {
        const br = c.businessRole;
        return br === "executor" || br === "both" || br === undefined; // legacy default = executor
      })
      .map((c) => {
        const cb30 = callbacks.filter(
          (k) => k.installerContractorIdAtCallback === c.id && new Date(k.filedAt).getTime() >= cutoff,
        ).length;
        const activeHbIds = new Set<string>();
        const releasedHbIds = new Set<string>();
        for (const e of holdbackLedger) {
          if (e.contractorId !== c.id) continue;
          if (e.action === "release" || e.action === "reverse") releasedHbIds.add(e.holdbackId);
          else activeHbIds.add(e.holdbackId);
        }
        releasedHbIds.forEach((id) => activeHbIds.delete(id));
        const score = computeExecutionScore({ contractor: c, jobs, callbacks }).score;
        return { c, cb30, holdbacks: activeHbIds.size, score };
      })
      .sort((a, b) => {
        // probation + low score first
        if (a.c.isOnProbation !== b.c.isOnProbation) return a.c.isOnProbation ? -1 : 1;
        const sa = a.score ?? 999;
        const sb = b.score ?? 999;
        return sa - sb;
      });
  }, [contractors, jobs, callbacks, holdbackLedger]);

  if (!canAccessExecutionDomain(role)) return null;

  return (
    <CockpitPage
      
      title="Installer Network"
      maxWidth="7xl"
    >
      {rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No installers in the network yet.
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contractor</TableHead>
                <TableHead>Vetting</TableHead>
                <TableHead>Probation</TableHead>
                <TableHead className="text-right">Jobs done</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead className="text-right">Callbacks 30d</TableHead>
                <TableHead className="text-right">Active holdbacks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ c, cb30, holdbacks, score }) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link to={`/execution/vetting/${c.id}`} className="text-[12px] font-medium hover:underline">
                      {c.companyName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">{c.vettingStatus ?? "—"}</Badge>
                  </TableCell>
                  <TableCell>
                    {c.isOnProbation
                      ? <Badge className="bg-warning/15 text-warning border-warning/30 text-[10px]">On probation</Badge>
                      : <span className="text-[11px] text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-[11px] tabular-nums">{c.jobsCompleted ?? 0}</TableCell>
                  <TableCell className="text-right text-[11px] tabular-nums">
                    {score === null
                      ? <span className="text-muted-foreground">— (sample &lt; {MIN_SAMPLE_FOR_DISPLAY})</span>
                      : score}
                  </TableCell>
                  <TableCell className={`text-right text-[11px] tabular-nums ${cb30 > 0 ? "text-warning font-semibold" : ""}`}>{cb30}</TableCell>
                  <TableCell className={`text-right text-[11px] tabular-nums ${holdbacks > 0 ? "text-warning font-semibold" : ""}`}>{holdbacks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </CockpitPage>
  );
}
